import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DatePicker } from "antd";
import moment from "moment-timezone";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { FaPlus, FaUserCircle } from "react-icons/fa";
import {
  getExamType,
  getGoal,
  getSingleStudent,
  userEditData,
  useUser,
} from "../../redux/services/authSlice";
import { onExportDetails } from "../../common/PdfFormat";
import { convertUtcToIstDateOnly } from "../../common/TimeUtils";
const EditProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = localStorage.getItem("userId");
  const singleUserData = useSelector((state) => state.auth?.singleUser);
  const getGoalData = useSelector((state) => state.auth?.goalData);
  const getExamTypeData = useSelector((state) => state.auth?.examTypeData);

  const [loading, setLoading] = useState(false);
  const initialValues = {
    userId: userId || user._id,
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    gender: undefined,
    dob: undefined,
    city: undefined,
    language: undefined,
    contact: {
      countryCode: "+91",
      mobile: undefined,
    },
    upscNumber: undefined,
    profileImage: undefined,
    goalId: undefined,
    examTypeId: [],
  };
  const validationSchema = Yup.object({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    gender: Yup.string().required("Gender is required"),
    dob: Yup.date().required("Date of Birth is required"),
    city: Yup.string().required("City is required"),
    language: Yup.string().required("Language is required"),
    contact: Yup.object({
      countryCode: Yup.string().required("Language is required"),
      mobile: Yup.string()
        .matches(/^\d{10}$/, "Mobile number must be 10 digits")
        .required("Mobile number is required"),
    }),
    upscNumber: Yup.string().required("UPSC Number is required"),
    goalId: Yup.string().required("Goal is required"),
    examTypeId: Yup.array()
      .of(Yup.string().required("Exam Type selected"))
      .min(1, "At least one Exam Type must be selected")
      .required("Exam Type are required"),
  });
  const onSubmit = async () => {
    setLoading(true);
    try {
      let response;
      response = await dispatch(userEditData(values));
      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message);
        navigate("/");
        resetForm();
      } else {
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setValues,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });
  const handleFileUpload = async (file) => {
    if (file.size > 1024 * 1024) {
      alert("File size should be less than 1MB.");
      return;
    }
    const formData = new FormData();
    formData.append("image", file);
    try {
      const response = await dispatch(fileUpload(formData));
      if (response) {
        setFieldValue("profileImage", response?.payload);
      }
    } catch (error) {
      console.error("File upload failed", error);
      alert("Error uploading file. Please try again.");
    }
  };

  const getUserDataById = useCallback(async () => {
    await dispatch(getSingleStudent(userId || user?._id));
  }, [dispatch, user, userId]);

  useEffect(() => {
    if (user) {
      getUserDataById();
    }
  }, [user, getUserDataById]);
  useEffect(() => {
    if (user && singleUserData) {
      setValues({
        userId: userId || user?._id,
        firstName: singleUserData?.firstName ?? undefined,
        lastName: singleUserData?.lastName ?? undefined,
        email: singleUserData?.email ?? undefined,
        gender: singleUserData?.gender ?? undefined,
        dob: moment(singleUserData?.dob)
          .tz("Asia/Kolkata")
          .format("YYYY-MM-DD"),
        city: singleUserData?.city ?? undefined,
        language: singleUserData?.language ?? undefined,
        upscNumber: singleUserData?.upscNumber ?? undefined,
        contact: {
          countryCode: singleUserData?.contact?.countryCode ?? undefined,
          mobile: singleUserData?.contact?.mobile ?? undefined,
        },
        profileImage: singleUserData?.profileImage ?? undefined,
        goalId: singleUserData?.goalId ?? undefined,
        examTypeId: singleUserData?.examTypeId ?? undefined,
      });
    }
  }, [singleUserData, setValues, user, userId]);
  const languageOption = [
    { value: "english", label: "English" },
    { value: "hindi", label: "Hindi" },
  ];
  const goalOptions = getGoalData?.map((goal) => ({
    value: goal._id,
    label: goal.name,
  }));
  const examTypeOptions = getExamTypeData?.map((goal) => ({
    value: goal._id,
    label: goal.name,
  }));
  const handleSelectAll = (selectedOptions) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFieldValue("examTypeId", selectedValues);
  };
  const genderOption = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
  ];
  const userFields = [
    {
      label: "Name",
      value: singleUserData?.firstName + " " + singleUserData?.lastName,
    },
    { label: "Email", value: singleUserData?.email },
    { label: "DOB", value: convertUtcToIstDateOnly(singleUserData?.dob) },
    {
      label: "Phone Number",
      value: singleUserData?.contact
        ? singleUserData?.contact?.countryCode +
          " " +
          singleUserData?.contact?.mobile
        : "",
    },
    { label: "Gender", value: singleUserData?.gender },
    { label: "City", value: singleUserData?.city },
    { label: "Referral Code", value: singleUserData?.referralCode },
    {
      label: "Friend Referral Code",
      value: singleUserData?.friendReferralCode,
    },
    {
      label: "Referral Code Amount",
      value:
        singleUserData?.refferalCodeAmount > 0
          ? singleUserData?.refferalCodeAmount
          : "",
    },
    {
      label: "Wallet Balance",
      value:
        singleUserData?.walletBalance > 0 ? singleUserData?.walletBalance : "",
    },
    {
      label: "Profile Image",
      value: singleUserData?.profileImage || "",
    },
  ];

  const onExport = () => {
    onExportDetails(userFields, singleUserData?.profileImage);
  };
  useEffect(() => {
    dispatch(getGoal());
    dispatch(getExamType());
  }, [dispatch]);

  return (
    <div className="md:px-6 my-3">
      <div>
        <div className="bg-gray-100 pb-4">
          <div className="w-full sm:mx-auto">
            <div className="px-3 py-7 bg-white shadow rounded-3xl sm:p-10">
              <div className="flex justify-between">
                <p className="text-3xl text-center pb-4 font-medium uppercase text-gray-900">
                  Edit Profile
                </p>
                <button
                  className="bg-[#124699] text-white px-4 py-2 rounded-lg font-semibold h-fit"
                  onClick={onExport}
                >
                  Export PDF
                </button>
              </div>
              <hr />
              <form className="mt-6" onSubmit={handleSubmit}>
                <div className="mt-3 flex justify-center">
                  <div className="relative w-32 h-32">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-gray-300 shadow-md">
                      {values?.profileImage ? (
                        <img
                          src={values.profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FaUserCircle className="text-gray-400 w-full h-full" />
                      )}
                    </div>

                    <label
                      htmlFor="imageUpload"
                      className="absolute bottom-1 right-2 bg-[#124699] text-white p-2 rounded-full shadow cursor-pointer hover:bg-[#124699]"
                    >
                      <FaPlus size={12} />
                    </label>

                    <input
                      id="imageUpload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        handleFileUpload(file);
                      }}
                    />
                  </div>
                </div>
                <div className="grid mt-5 md:grid-cols-2 grid-cols-1 gap-5">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="First Name"
                    />
                    {touched.firstName && errors.firstName && (
                      <div className="text-red-500 text-sm">
                        {errors.firstName}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Last Name"
                    />
                    {touched.lastName && errors.lastName && (
                      <div className="text-red-500 text-sm">
                        {errors.lastName}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Email"
                    />
                    {touched.email && errors.email && (
                      <div className="text-red-500 text-sm">{errors.email}</div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Date of Birth
                    </label>
                    <DatePicker
                      className="w-full px-4 py-2 border border-gray-300 rounded-md !outline-none"
                      format="YYYY-MM-DD"
                      value={values.dob ? moment(values.dob) : null}
                      onChange={(_, dateString) =>
                        setFieldValue("dob", dateString)
                      }
                      onBlur={handleBlur}
                      placeholder="Select Date"
                    />
                    {touched.dob && errors.dob && (
                      <div className="text-red-500 text-sm">{errors.dob}</div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md"
                      value={values.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="City"
                    />
                    {touched.city && errors.city && (
                      <div className="text-red-500 text-sm">{errors.city}</div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Gender
                    </label>
                    <Select
                      name="gender"
                      className="w-full text-base mt-1 h-[40px] rounded-md focus:border-[#124699]"
                      value={genderOption?.find(
                        (option) => option.value === values.gender
                      )}
                      onChange={(e) =>
                        setFieldValue("gender", e ? e.value : "")
                      }
                      options={genderOption}
                      classNamePrefix="custom-select"
                    />
                    {touched.gender && errors.gender && (
                      <div className="text-red-500 text-sm">
                        {errors.gender}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      UPSC Number
                    </label>
                    <input
                      type="text"
                      name="upscNumber"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md"
                      value={values.upscNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="UPSC Number"
                    />
                    {touched.upscNumber && errors.upscNumber && (
                      <div className="text-red-500 text-sm">
                        {errors.upscNumber}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Language
                    </label>
                    <Select
                      name="language"
                      className="w-full text-base mt-1 h-[40px] rounded-md focus:border-[#124699]"
                      value={languageOption?.find(
                        (option) => option.value === values.language
                      )}
                      onChange={(e) =>
                        setFieldValue("language", e ? e.value : "")
                      }
                      options={languageOption}
                      classNamePrefix="custom-select"
                    />
                    {touched.language && errors.language && (
                      <div className="text-red-500 text-sm">
                        {errors.language}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Goal
                    </label>
                    <Select
                      name="goalId"
                      className="w-full text-base mt-1 h-[40px] rounded-md focus:border-[#124699]"
                      value={goalOptions?.find(
                        (option) => option.value === values.goalId
                      )}
                      onChange={(e) =>
                        setFieldValue("goalId", e ? e.value : "")
                      }
                      options={goalOptions}
                      classNamePrefix="custom-select"
                    />
                    {touched.goalId && errors.goalId && (
                      <div className="text-red-500 text-sm">
                        {errors.goalId}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Exam Type
                    </label>
                    <Select
                      isMulti
                      name="examTypeId"
                      className="w-full text-base mt-1 h-[40px] rounded-md focus:border-[#124699]"
                      value={examTypeOptions?.filter((option) =>
                        values.examTypeId?.includes(option.value)
                      )}
                      onChange={handleSelectAll}
                      onBlur={handleBlur}
                      maxMenuHeight={200}
                      options={examTypeOptions}
                      classNamePrefix="custom-select"
                    />
                    {touched.examTypeId && errors.examTypeId && (
                      <div className="text-red-500 text-sm">
                        {errors.examTypeId}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Contact
                    </label>
                    <div className="mt-1">
                      <div className="flex gap-3">
                        <PhoneInput
                          international
                          defaultCountry="IN"
                          value={values.contact.countryCode}
                          onChange={(value) => {
                            setFieldValue("contact.countryCode", value);
                          }}
                          onBlur={handleBlur}
                          className="w-[100px] px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                          placeholder="Country Code"
                        />
                        <div className="w-full">
                          <input
                            type="text"
                            name="contact.mobile"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            value={values.contact.mobile}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Mobile Number"
                          />
                        </div>
                      </div>
                      {touched.contact?.mobile && errors.contact?.mobile && (
                        <div className="text-red-500 text-sm">
                          {errors.contact?.mobile}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <button
                    type="submit"
                    className="w-[250px] h-[50px] flex justify-center font-semibold text-lg items-center text-white bg-[#124699] rounded-md hover:bg-[#124699] mt-7"
                  >
                    {loading ? <div className="loader"></div> : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
